import { getAuthClient } from "@trumpf/ux.utils";
import { i18nProvider } from "@trumpf/ux.i18n";
import { NotificationProviderScope } from "../interface";
import { registerNotificationBackend } from "../backend";
import { fetchNotifications, fetchCategories, mapNotification } from "./api";
import { MyTrumpfEnvironmentEnum } from "./interface";
import { NOTIFICATIONS_POLL_INTERVAL_DEFAULT, NOTIFICATIONS_POLL_INTERVAL_MIN } from "./constants";
const NO_TOKEN_ERROR = {
    status: 401,
    message: "no_token"
};
export class MyTrumpfNotificationBackend {
    static getInstance() {
        return MyTrumpfNotificationBackend._instance;
    }
    constructor(options = {}) {
        this._categories = [];
        this._pollIncrement = 0;
        this.debug = false;
        this._log = (msg) => {
            if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
                /* eslint-disable-next-line no-console */
                console.log(`[MyTrumpfNotificationBackend]: ${msg}`);
            }
        };
        this.handleI18n = ({ language }) => {
            this._log(`language changed: "${language}"`);
            this.startPoll();
        };
        this.handleAuthClient = () => {
            this._log("token changed");
            this.startPoll();
        };
        this.updateCategories = async () => {
            const response = await fetchCategories({
                stage: this._environment,
                token: this._authClient.getAccessToken(),
                locale: i18nProvider.language
            });
            if (response.ok) {
                const data = await response.json();
                this._categories = data.categories;
            }
            else {
                this._categories = [];
            }
        };
        this.startPoll = () => {
            window.clearTimeout(this._pollTimeout);
            if (this._provider === undefined) {
                this._log("[SKIP] polling: no provider");
                return;
            }
            // reset notification provider state
            this._provider.setError(null);
            this._provider.setNotifications([]);
            this._provider.setPending(true);
            const token = this._authClient.getAccessToken();
            if (token === null) {
                this._log("[SKIP] polling: no token");
                this._provider.setError(NO_TOKEN_ERROR);
                this._provider.setPending(false);
                return;
            }
            this.updateCategories().then(() => {
                this._log("start polling …");
                this.poll();
            });
        };
        this.poll = async () => {
            var _a;
            window.clearTimeout(this._pollTimeout);
            const provider = this._provider;
            const token = this._authClient.getAccessToken();
            const { language } = i18nProvider;
            if (!provider) {
                this._log("[SKIP] poll: no provider");
                return;
            }
            if (token === null) {
                this._log("[SKIP] request: no token");
                provider.setNotifications([]);
                provider.setError(NO_TOKEN_ERROR);
                provider.setPending(false);
                return;
            }
            provider.setError(null);
            provider.setPending(true);
            this._pollIncrement++;
            const pollIncrement = this._pollIncrement;
            this._log(`request #${pollIncrement} (locale: "${language}" stage: "${this._environment}")`);
            const response = await fetchNotifications({
                stage: this._environment,
                token,
                locale: language
            });
            if (pollIncrement !== this._pollIncrement || provider !== this._provider || token !== this._authClient.getAccessToken()) {
                this._log(`[SKIP] ignore response #${pollIncrement}`);
                return;
            }
            if (response.ok) {
                const data = await response.json();
                this._log(`response #${pollIncrement} ok (${(_a = data.notifications) === null || _a === void 0 ? void 0 : _a.length} notifications)`);
                const notifications = Array.isArray(data.notifications)
                    ? data.notifications.map((notification) => mapNotification(notification, this._categories))
                    : [];
                provider.setNotifications(notifications);
            }
            else {
                this._log(`response #${pollIncrement} not ok (status: ${response.status} statusText: "${response.statusText}")`);
                provider.setError({
                    status: response.status,
                    message: response.statusText
                });
            }
            provider.setPending(false);
            if (response.ok) {
                this._log(`polling again in ${this._pollInterval} ms …`);
                this._pollTimeout = window.setTimeout(this.poll, this._pollInterval);
            }
            else {
                this._log(`stop polling`);
            }
        };
        this.init(options);
    }
    get authClient() {
        return this._authClient;
    }
    get environment() {
        return this._environment;
    }
    get pollInterval() {
        return this._pollInterval;
    }
    get provider() {
        return this._provider;
    }
    init(options = {}) {
        if (MyTrumpfNotificationBackend._instance !== null) {
            MyTrumpfNotificationBackend._instance.destroy();
        }
        MyTrumpfNotificationBackend._instance = this;
        this.debug = options.debug || false;
        this._log("initialized");
        this._environment = options.environment || MyTrumpfEnvironmentEnum.Production;
        this._i18nSubscriber = i18nProvider.subscribe(this.handleI18n);
        this._authClient = getAuthClient(options.authClientName);
        this._authClient.subscribe(this.handleAuthClient);
        this._pollInterval =
            typeof options.pollInterval === "number" && !Number.isNaN(options.pollInterval)
                ? Math.max(options.pollInterval, NOTIFICATIONS_POLL_INTERVAL_MIN)
                : NOTIFICATIONS_POLL_INTERVAL_DEFAULT;
    }
    destroy() {
        if (MyTrumpfNotificationBackend._instance === this) {
            MyTrumpfNotificationBackend._instance = null;
            this._log("destroyed");
        }
        i18nProvider.unsubscribe(this._i18nSubscriber);
        this._authClient.unsubscribe(this.handleAuthClient);
        this._provider = undefined;
    }
    initProvider(provider) {
        if (this._provider !== undefined) {
            return;
        }
        this._log(`init provider`);
        this._provider = provider;
        this.startPoll();
    }
}
MyTrumpfNotificationBackend._instance = null;
export const initMyTrumpfNotificationBackend = (options) => {
    const backend = new MyTrumpfNotificationBackend(options);
    registerNotificationBackend(backend, NotificationProviderScope.MyTrumpf);
    return backend;
};
//# sourceMappingURL=backend.js.map
